'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/navigation';

import {
  LayoutDashboard, Users, ClipboardList, BarChart3,
  ChevronLeft, ChevronRight, LogOut, Settings, Bus,
  CalendarOff, Bell, SlidersHorizontal, FileBarChart2,
  ShieldCheck, TriangleAlert, MapPinned, FileText,
  Sun, Moon, Trophy, ShieldAlert, Wallet, Brain, UserSquare2,
  ClipboardCheck, ScrollText, Receipt, History, PieChart, Sparkles, AlarmClock } from
'lucide-react';
import { useTheme } from '@/lib/ThemeContext';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { notificationService } from '@/lib/supabase/services/notificationService';


const navGroups = [
{
  label: 'Operations',
  items: [
  { href: '/operations-dashboard', label: 'Dashboard', icon: LayoutDashboard, badge: null },
  { href: '/duty-assignment', label: 'Duty Assignment', icon: ClipboardList, badge: null },
  { href: '/route-planning', label: 'Route Planning', icon: MapPinned, badge: null },
  { href: '/notifications', label: 'Notifications', icon: Bell, badge: 'notifications' },
  { href: '/ai-assistant', label: 'AI Assistant', icon: Sparkles, badge: null }]

},
{
  label: 'Crew & Fleet',
  items: [
  { href: '/crew-dashboard', label: 'Crew Dashboard', icon: UserSquare2, badge: null },
  { href: '/crew-management', label: 'Crew Management', icon: Users, badge: null },
  { href: '/driver-intelligence', label: 'Driver Intelligence', icon: Brain, badge: null },
  { href: '/route-fleet-management', label: 'Routes & Fleet', icon: Bus, badge: null },
  { href: '/shift-management', label: 'Shift Management', icon: AlarmClock, badge: null },
  { href: '/leave-management', label: 'Leave Management', icon: CalendarOff, badge: null },
  { href: '/leave-review', label: 'Leave Review', icon: ClipboardCheck, badge: null },
  { href: '/shift-reports', label: 'Shift Reports', icon: FileBarChart2, badge: null },
  { href: '/post-duty-feedback', label: 'Post-Duty Feedback', icon: ClipboardCheck, badge: null },
  { href: '/license-management', label: 'License', icon: ShieldCheck, badge: null },
  { href: '/violations', label: 'Violations', icon: TriangleAlert, badge: null },
  { href: '/rewards', label: 'Rewards', icon: Trophy, badge: null },
  { href: '/accident-incident-register', label: 'Accident & Incident', icon: ShieldAlert, badge: null },
  { href: '/bhatta-management', label: 'Bhatta Management', icon: Wallet, badge: null },
  { href: '/bhatta-payroll', label: 'Bhatta Payroll', icon: Receipt, badge: null }]

},
{
  label: 'Analytics',
  items: [
  { href: '/duty-history-performance', label: 'History & Performance', icon: BarChart3, badge: null },
  { href: '/bhatta-analytics', label: 'Bhatta Analytics', icon: PieChart, badge: null },
  { href: '/reports', label: 'Reports', icon: FileText, badge: null }]

},
{
  label: 'Administration',
  items: [
  { href: '/audit-log', label: 'Audit Log', icon: ScrollText, badge: null },
  { href: '/payment-audit-history', label: 'Payment Audit History', icon: History, badge: null },
  { href: '/system-config', label: 'System Config', icon: SlidersHorizontal, badge: null },
  { href: '/settings', label: 'Settings', icon: Settings, badge: null }]

}];


export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const pathname = usePathname();
  const { theme, toggleTheme } = useTheme();
  const { user, signOut } = useAuth();
  const router = useRouter();

  // Load unread notification count and subscribe to real-time changes
  useEffect(() => {
    notificationService.getUnreadCount().then(setUnreadCount);
    const unsub = notificationService.subscribeToChanges((notifications) => {
      const count = notifications.filter((n) => !n.isRead).length;
      setUnreadCount(count);
    });
    return unsub;
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
      router.replace('/sign-up-login-screen');
    } catch {
      router.replace('/sign-up-login-screen');
    }
  };

  // Derive display name and initials from user metadata or email
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const role = user?.user_metadata?.role || 'Administrator';
  const initials = displayName.
  split(' ').
  map((n: string) => n[0]).
  join('').
  toUpperCase().
  slice(0, 2);

  return (
    <aside
      className={`relative flex flex-col shrink-0 sidebar-transition z-20 ${
      collapsed ? 'w-16' : 'w-60'}
      bg-white/80 dark:bg-[#0a0a0a] border-r border-[var(--glass-border)] dark:border-white/[0.06]
      backdrop-blur-xl shadow-sm dark:shadow-[4px_0_24px_rgba(0,0,0,0.5)]`
      }>

      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-4 border-b border-[var(--glass-border)] dark:border-white/[0.06] h-16 ${collapsed ? 'justify-center px-2' : ''}`}>
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-indigo-500 dark:from-indigo-500 dark:to-violet-600 flex items-center justify-center shadow-lg shadow-primary/30 dark:shadow-indigo-500/20 shrink-0">
          <Bus size={16} className="text-white" />
        </div>
        {!collapsed &&
        <div className="min-w-0">
            <span className="font-700 text-foreground tracking-tight block text-base dark:text-white">BusEasy</span>
            <span className="text-[10px] font-600 tracking-widest uppercase text-muted-foreground dark:text-indigo-400/70">CREW MANAGER</span>
          </div>
        }
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin px-2">
        {navGroups?.map((group) =>
        <div key={`group-${group?.label}`} className="mb-4">
            {!collapsed &&
          <p className="px-2 mb-1.5 text-[10px] font-700 uppercase tracking-widest text-muted-foreground/60 dark:text-slate-500 select-none">
                {group?.label}
              </p>
          }
            {group?.items?.map((item) => {
            const Icon = item?.icon;
            const active = pathname === item?.href || item?.href === '/operations-dashboard' && pathname === '/';
            const badgeValue = item?.badge === 'notifications'
              ? (unreadCount > 0 ? String(unreadCount > 99 ? '99+' : unreadCount) : null)
              : item?.badge;
            return (
              <Link
                key={`nav-${item?.href}`}
                href={item?.href}
                title={collapsed ? item?.label : undefined}
                className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5 transition-all duration-200 text-sm font-500 relative
                    ${active ?
                'bg-primary/10 text-primary dark:bg-indigo-500/15 dark:text-indigo-300 shadow-sm shadow-primary/10 dark:shadow-indigo-500/10' :
                'text-muted-foreground dark:text-slate-400 hover:bg-black/5 dark:hover:bg-white/[0.06] hover:text-foreground dark:hover:text-slate-100'}
                    ${
                collapsed ? 'justify-center px-2' : ''}
                  `}>

                  {active &&
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary dark:bg-indigo-400 rounded-r-full" />
                }
                  <Icon size={17} className="shrink-0" />
                  {!collapsed && <span className="flex-1 truncate">{item?.label}</span>}
                  {!collapsed && badgeValue &&
                <span className="bg-negative text-white text-[10px] font-700 px-1.5 py-0.5 rounded-full tabular-nums min-w-[18px] text-center">
                      {badgeValue}
                    </span>
                }
                  {collapsed && badgeValue &&
                <span className="absolute top-1 right-1 w-2 h-2 bg-negative rounded-full ring-2 ring-background" />
                }
                </Link>);

          })}
          </div>
        )}
      </nav>

      {/* Bottom */}
      <div className="border-t border-[var(--glass-border)] dark:border-white/[0.06] p-2 space-y-0.5">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          title={collapsed ? theme === 'dark' ? 'Light Mode' : 'Dark Mode' : undefined}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-500 text-muted-foreground dark:text-slate-400 hover:bg-black/5 dark:hover:bg-white/[0.06] hover:text-foreground dark:hover:text-slate-100 transition-all duration-200 ${collapsed ? 'justify-center px-2' : ''}`}>

          {theme === 'dark' ? <Sun size={17} className="shrink-0 text-amber-400" /> : <Moon size={17} className="shrink-0" />}
          {!collapsed && <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
        </button>

        <Link
          href="/system-config"
          title={collapsed ? 'Settings' : undefined}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-500 text-muted-foreground dark:text-slate-400 hover:bg-black/5 dark:hover:bg-white/[0.06] hover:text-foreground dark:hover:text-slate-100 transition-all duration-200 ${collapsed ? 'justify-center px-2' : ''}`}>

          <Settings size={17} className="shrink-0" />
          {!collapsed && <span>Settings</span>}
        </Link>

        <button
          onClick={handleSignOut}
          title={collapsed ? 'Sign Out' : undefined}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-500 text-muted-foreground dark:text-slate-400 hover:bg-black/5 dark:hover:bg-white/[0.06] hover:text-foreground dark:hover:text-slate-100 transition-all duration-200 ${collapsed ? 'justify-center px-2' : ''}`}>

          <LogOut size={17} className="shrink-0" />
          {!collapsed && <span>Sign Out</span>}
        </button>

        {/* User profile */}
        <div className={`flex items-center gap-2.5 px-3 py-2.5 mt-1 rounded-xl bg-primary/8 dark:bg-indigo-500/10 dark:border dark:border-indigo-500/20 ${collapsed ? 'justify-center px-2' : ''}`}>
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary to-indigo-500 dark:from-indigo-500 dark:to-violet-600 flex items-center justify-center text-white text-xs font-700 shrink-0 shadow-sm">
            {initials}
          </div>
          {!collapsed &&
          <div className="min-w-0">
              <p className="text-sm font-600 text-foreground dark:text-slate-100 truncate">{displayName}</p>
              <p className="text-[10px] text-muted-foreground dark:text-slate-500 font-500">{role}</p>
            </div>
          }
        </div>
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-white dark:bg-[#1a1a1a] border border-[var(--glass-border)] dark:border-white/10 flex items-center justify-center shadow-md dark:shadow-black/50 hover:scale-110 transition-all duration-200 z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>

        {collapsed ? <ChevronRight size={11} className="text-muted-foreground dark:text-slate-400" /> : <ChevronLeft size={11} className="text-muted-foreground dark:text-slate-400" />}
      </button>
    </aside>);

}